import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken } from '../api/client';
import { OrderStatus } from '../api/orders';

export interface TrackingEvent {
  trackingCode: string;
  status: OrderStatus;
  at: string;
}

/**
 * S'abonne aux events `orderStatusChanged` pour un trackingCode donné.
 * Retourne la liste des events reçus pendant la session (les plus récents en tête).
 *
 * Auth : utilise le token JWT en localStorage (injecté via handshake.auth.token).
 * Connexion : utilise l'origin courant (proxy Vite s'occupe du upgrade WS en dev).
 */
export const useOrderTracking = (trackingCode: string | undefined) => {
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!trackingCode) return;
    const token = getToken();
    if (!token) return;

    const socketUrl = import.meta.env.VITE_SOCKET_IO_URL || '/';
    const socket: Socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true
    });

    socket.on('connect', () => {
      setConnected(true);
      setError(null);
      socket.emit('subscribeOrder', trackingCode);
    });

    socket.on('connect_error', (err) => {
      setConnected(false);
      setError(err.message);
    });

    socket.on('disconnect', () => setConnected(false));

    socket.on('orderStatusChanged', (payload: TrackingEvent) => {
      if (payload.trackingCode !== trackingCode) return;
      setEvents((prev) => [payload, ...prev].slice(0, 20));
    });

    return () => {
      socket.emit('unsubscribeOrder', trackingCode);
      socket.disconnect();
    };
  }, [trackingCode]);

  return { events, connected, error };
};
